import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import SkeletonPlaceholder from 'react-native-skeleton-placeholder';
import { useTheme, type Theme } from '../../theme';
import { borderWidth, s, v } from '../../utils/libs';

type OrdersListSkeletonProps = {
  count?: number;
};

/** Matches `CompletedOrders` / `PendingOrders` / `PartialOrders`: one top row (name+items | total), one bottom (date | receipt). */
const OrdersListSkeleton: React.FC<OrdersListSkeletonProps> = ({
  count = 2,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const rows = Array.from({ length: Math.max(1, count) });

  return (
    <SkeletonPlaceholder
      backgroundColor={theme.colors.border}
      highlightColor={theme.colors.surfaceSecondary}>
      <View style={styles.list}>
        {rows.map((_, idx) => (
          <View
            key={String(idx)}
            style={[
              styles.section,
              idx < rows.length - 1 ? null : styles.sectionLast,
            ]}>
            <View style={styles.top}>
              <View style={styles.nameItemsBar} />
              <View style={styles.totalPriceBar} />
            </View>
            <View style={styles.bottom}>
              <View style={styles.dateBar} />
              <View style={styles.receiptBar} />
            </View>
          </View>
        ))}
      </View>
    </SkeletonPlaceholder>
  );
};

export default OrdersListSkeleton;

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    list: {
      width: '100%',
    },
    section: {
      borderBottomWidth: borderWidth(1),
      borderColor: theme.colors.border,
      paddingHorizontal: s(5),
      paddingTop: v(5),
      paddingBottom: v(8),
    },
    sectionLast: {
      borderBottomWidth: 0,
    },
    top: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingRight: s(2),
    },
    nameItemsBar: {
      width: '62%',
      height: v(16),
      borderRadius: theme.borderRadius.sm,
    },
    totalPriceBar: {
      width: s(120),
      height: v(16),
      borderRadius: theme.borderRadius.sm,
    },
    bottom: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginTop: v(10),
      paddingRight: s(2),
    },
    dateBar: {
      width: '38%',
      height: v(12),
      borderRadius: theme.borderRadius.sm,
    },
    receiptBar: {
      width: '48%',
      height: v(12),
      borderRadius: theme.borderRadius.sm,
    },
  });
